import { Injectable } from '@angular/core';

import { HttpClient, HttpHeaders } from '@angular/common/http';

import { ToastrService } from 'ngx-toastr';
import { apiUrl } from '../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class LookupService {

  response:any;
  apiLink = apiUrl+'/api/lookup/';

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private authService: AuthService,
  ) { }

  // Handle Server Call
  async handleGetRequest(url) {
    return this.http.get(url).toPromise().then(response => {
      this.response = response;
      if(this.response.status == 0) {
        this.toastr.error(this.response.msg, 'Error!', {
          timeOut: 5000,
        });
        return false;
      }

      return this.response.data;
    }).catch(err => {
      console.error(err);
      this.toastr.error('Could not connect to server. Please check your network connection.', 'Network Error!', {
        timeOut: 5000,
      });
      return false;
    });
  }
  
  // Global Locations
  async getGlobalLocations(dependent = 0, loc_type: string | null = null, loc_parent_id: string | null = null) {
    // Construct URL
    let url = this.apiLink+`get_global_location?dependent=${dependent}&loc_type=${loc_type}`;
    if(loc_parent_id) {
      url = url + `&loc_parent_id=${loc_parent_id}`;
    }
    
    return this.handleGetRequest(url);
  }

  // Org Locations
  async getOrgLocations(dependent = 0, org_code: string, loc_type: string | null = null, loc_parent_id: string | null = null, loc_parent_type: string | null = null) {
    // Construct URL
    let url = this.apiLink+`get_location?dependent=${dependent}&org_code=${org_code}&loc_type=${loc_type}`;
    if(loc_parent_id) {
      url = url + `&loc_parent_id=${loc_parent_id}`;
    }
    if(loc_parent_type) {
      url = url + `&loc_parent_type=${loc_parent_type}`;
    }
    
    return this.handleGetRequest(url);
  }
  
  // Project Locations
  async getProjLocations(dependent = 0, org_code: string, project_id: string, loc_type: string | null = null, loc_parent_id: string | null = null) {
    // Construct URL
    let url = this.apiLink+`get_proj_location?dependent=${dependent}&org_code=${org_code}&project_id=${project_id}&loc_type=${loc_type}`;
    if(loc_parent_id) {
      url = url + `&loc_parent_id=${loc_parent_id}`;
    }
    
    return this.handleGetRequest(url);
  }

  // User Locations
  async getUserLocations(dependent = 0, org_code: string, project_id: string, user_id: string, user_role: string, loc_type: string | null = null, loc_parent_id: string | null = null) {
    // Construct URL
    let url = this.apiLink+`get_user_location?dependent=${dependent}&org_code=${org_code}&user_id=${user_id}&role_id=${user_role}&loc_type=${loc_type}`;
    if(project_id) {
      url = url + `&project_id=${project_id}`;
    }
    if(loc_parent_id) {
      url = url + `&loc_parent_id=${loc_parent_id}`;
    }
    
    return this.handleGetRequest(url);
  }

  // Beneficiary Group Locations
  async getBenefGroupLocations(dependent = 0, org_code: string, group_id: string, loc_type: string | null = null, loc_parent_id: string | null = null) {
    // Construct URL
    let url = this.apiLink+`get_benef_group_location?dependent=${dependent}&org_code=${org_code}&group_id=${group_id}&loc_type=${loc_type}`;
    if(loc_parent_id) {
      url = url + `&loc_parent_id=${loc_parent_id}`;
    }
    
    return this.handleGetRequest(url);
  }

  // Organization FPOs
  async getOrgFPOs(org_code: string) {
    // Construct URL
    let url = this.apiLink+`get_fpo?org_code=${org_code}`;
    
    return this.handleGetRequest(url);
  }

  // Organization Clients
  async getClients(org_code: string) {
    // Construct URL
    let url = this.apiLink+`get_crm_client?org_code=${org_code}`;
    
    return this.handleGetRequest(url);
  }

  // Organization Programs
  async getPrograms(org_code: string, project_id:any = null) {
    // Construct URL
    let url = this.apiLink+`get_program?org_code=${org_code}`;
    if(project_id) {
      url = url + `&project_id=${project_id}`;
    }
    
    return this.handleGetRequest(url);
  }

  // Organization Programs tagged to Projects
  async getProgramsWithProjects(org_code: string, project_id:any = null) {
    // Construct URL
    let url = this.apiLink+`get_program_with_project?org_code=${org_code}`;
    if(project_id) {
      url = url + `&project_id=${project_id}`;
    }
    
    return this.handleGetRequest(url);
  }

  // Organization Projects
  async getProjects(org_code: string, project_id:any = null, user_id:any = null) {
    // Construct URL
    let url = this.apiLink+`get_project?org_code=${org_code}`;
    if(project_id) {
      url = url + `&project_id=${project_id}`;
    }
    if(user_id) {
      url = url + `&user_id=${user_id}`;
    }
    
    return this.handleGetRequest(url);
  }

  async getMilestones(org_code: string, project_id:any = null) {
    // Construct URL
    let url = this.apiLink+`get_milestone?org_code=${org_code}`;
    if(project_id) {
      url = url + `&project_id=${project_id}`;
    }
    
    return this.handleGetRequest(url);
  }
  async getSubMilestones(org_code: string, project_id:any = null, milestone_id:any = null) {
    // Construct URL
    let url = this.apiLink+`get_tasklist?org_code=${org_code}`;
    if(project_id) {
      url = url + `&project_id=${project_id}`;
    }
    if(milestone_id) {
      url = url + `&milestone_id=${milestone_id}`;
    }

    return this.handleGetRequest(url);
  }

  // Organization Assigned Tasks
  async getAssignedTasks(user_id:any) {
    // Construct URL
    let url = this.apiLink+`get_assigned_task?user_id=${user_id}`;
    
    return this.handleGetRequest(url);
  }

  async getActivities(org_code: string, project_id:any = null) {
    // Construct URL
    let url = this.apiLink+`get_activities?org_code=${org_code}`;
    if(project_id) {
      url = url + `&project_id=${project_id}`;
    }

    return this.handleGetRequest(url);
  }

  // F13 — the logframe build-order guide counts what exists at each level.
  async getOutputs(org_code: string) {
    return this.handleGetRequest(this.apiLink + `get_outputs?org_code=${org_code}`);
  }

  async getOutcomes(org_code: string) {
    return this.handleGetRequest(this.apiLink + `get_outcomes?org_code=${org_code}`);
  }

  async getUsers(org_code: string, project_id:any = null) {
    // Construct URL
    let url = this.apiLink+`get_users?org_code=${org_code}`;
    if(project_id) {
      url = url + `&project_id=${project_id}`;
    }

    return this.handleGetRequest(url);
  }

  // SDGs
  async getSDGs(org_code: any = null) {
    // Construct URL
    let url = this.apiLink+`get_sdg`;
    if(org_code && org_code.length > 0) {
      url = url + `?org_code=${org_code}`;
    }
    
    return this.handleGetRequest(url);
  }

  // Sectors
  async getSectors(org_code: any = null) {
    // Construct URL
    let url = this.apiLink+`get_sector`;
    if(org_code && org_code.length > 0) {
      url = url + `?org_code=${org_code}`;
    }

    return this.handleGetRequest(url);
  }

  /**
   * The org's assigned thematic areas, in tree order, keyed by `global_sector.id`.
   *
   * This is the picker behind every sector field on the logframe. Use it, NOT getSectors():
   * that one returns raw org_sector rows whose `id` is thrown away and regenerated every time
   * an admin re-assigns the org's sectors, so it is not safe to store. This returns the stable
   * global id with the org's own label. Each row carries a `depth` for the indent.
   *
   * Authed — the org comes from the token, not from a query string.
   */
  async getOrgSectorTree() {
    let userDetails = await this.authService.getUserDetails(),
    token = userDetails?.token ? userDetails.token : '';

    let headers = new HttpHeaders({
      'Authorization': `Bearer: ${token}`
    });

    return this.http.get(this.apiLink+`org_sector_tree`, {headers: headers}).toPromise().then(response => {
      this.response = response;
      if(this.response.status == 0) {
        this.toastr.error(this.response.msg, 'Error!', { timeOut: 5000 });
        return [];
      }
      // `label` is the indented display string for a <select>. Built here, once, rather than in
      // each of the four templates that need it: interpolation escapes HTML, so an `&nbsp;` in the
      // template would render as the literal text "&nbsp;". These are real non-breaking spaces.
      return (this.response.data || []).map(s => ({
        ...s,
        label: '   '.repeat(s.depth || 0) + s.name
      }));
    }).catch(err => {
      console.error(err);
      // A missing sector list must not take the whole form down — the field is optional.
      return [];
    });
  }

  // Currencies
  async getCurrencies() {
    // Construct URL
    let url = this.apiLink+`get_currency`;
    
    return this.handleGetRequest(url);
  }

  // IRIS
  async getIRIS() {
    // Construct URL
    let url = this.apiLink+`get_iris`;
    
    return this.handleGetRequest(url);
  }

  async getAssemblyConstituency(org_code = 'XXXXXX') {
    // Construct URL
    let url = this.apiLink+`get_assembly_constituency?org_code=${org_code}`;
    
    return this.handleGetRequest(url);
  }
  async getParliamentaryConstituency(org_code = 'XXXXXX') {
    // Construct URL
    let url = this.apiLink+`get_parliamentary_constituency?org_code=${org_code}`;
    
    return this.handleGetRequest(url);
  }
  async getPollingBooths(org_code = 'XXXXXX') {
    // Construct URL
    let url = this.apiLink+`get_polling_booth?org_code=${org_code}`;
    
    return this.handleGetRequest(url);
  }
}
